import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { Popconfirm } from 'antd';
import { useGetAllListingEnrichedQuery, useDeleteListingMutation } from '../../services/Listing';
import { mapListing, getVerdictFromRules } from '../../utils/mapListing';
import EditObjectModal from '../Common/EditObjectModal';
import { useRules } from '../../hooks/useRules';

const CYAN             = '#1DB3B3';
const CYAN_HOVER       = '#0E7A6B';
const CYAN_ACTIVE_BG   = 'rgba(29,179,179,0.08)';
const CYAN_ACTIVE_BORDER = '#1DB3B3';

const MAX_COMPARE = 4;

const fmt   = (n) => n.toLocaleString('de-DE');
const fmtCf = (v) => (v >= 0 ? '+' : '') + v.toLocaleString('de-DE') + ' €';

/* ── Score Ring ── */
export function ScoreRing({ score, size = 60, sw = 5 }) {
  const rules = useRules();
  const tokens = rules.color_tokens;
  const r   = size / 2 - sw;
  const c   = size / 2;
  const ci  = 2 * Math.PI * r;
  const off = ci * (1 - (score || 0) / 100);
  const color = score >= 65 ? tokens['primary-cyan'] : score >= 40 ? tokens['value-orange'] : score !== null && score !== undefined ? tokens['value-red'] : undefined;
  const bg    = score >= 65 ? tokens['cyan-light']   : score >= 40 ? '#fef3c7'               : score !== null && score !== undefined ? '#fee2e2'              : '#f1f5f9';

  return (
    <div className="relative shrink-0 animate-score-in" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={c} cy={c} r={r} fill="none" stroke={bg} strokeWidth={sw} />
        <circle cx={c} cy={c} r={r} fill="none" stroke={color || '#e2e8f0'} strokeWidth={sw} strokeLinecap="round"
          strokeDasharray={ci.toFixed(2)} strokeDashoffset={off.toFixed(2)}
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(.16,1,.3,1)' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-sans text-lg font-semibold text-gray-900 leading-none" style={{ fontSize: size * 0.32 }}>
          {score ?? '\u2014'}
        </span>
        {score !== null && score !== undefined && (
          <span className="text-gray-400 leading-none" style={{ fontSize: size * 0.15 }}>/ 100</span>
        )}
      </div>
    </div>
  );
}

export { fmt as fmtPrice, fmtCf };

/* ── Kebab Menu (3-Dot) ── */
function KebabMenu({ deal, onAddToCompare, onDelete }) {
  const [open, setOpen]       = useState(false);
  const [popOpen, setPopOpen] = useState(false);
  const menuRef               = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div
      ref={menuRef}
      style={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Trigger */}
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#8C8FA0',
          padding: 0,
          transition: 'background 150ms, color 150ms',
          flexShrink: 0,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#F3F3F5'; e.currentTarget.style.color = '#5D5D6D'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#8C8FA0'; }}
        title="Optionen"
        aria-label="Optionen"
      >
        {/* Vertical 3-dot icon 16x16 */}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="3.5"  r="1.2" fill="currentColor"/>
          <circle cx="8" cy="8"    r="1.2" fill="currentColor"/>
          <circle cx="8" cy="12.5" r="1.2" fill="currentColor"/>
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute',
          top: 32,
          right: 0,
          background: '#FFFFFF',
          border: '1px solid #E0E0DF',
          borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
          minWidth: 196,
          zIndex: 100,
          overflow: 'hidden',
        }}>
          {/* Zum Vergleich hinzufügen */}
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); onAddToCompare(deal.id); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              padding: '10px 14px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 500,
              color: '#1DB3B3',
              textAlign: 'left',
              transition: 'background 120ms',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F3F3F5'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
              <rect x="1" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/>
              <rect x="9" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/>
              <rect x="1" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M11.5 9v6M8.5 12h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            Zum Vergleich hinzufügen
          </button>

          {/* Separator */}
          <div style={{ height: 1, background: '#E0E0DF' }} />

          {/* Löschen — triggers Popconfirm */}
          <Popconfirm
            title="Deal löschen?"
            description="Dieser Deal wird unwiderruflich entfernt."
            open={popOpen}
            onConfirm={(e) => { e?.stopPropagation?.(); setPopOpen(false); setOpen(false); onDelete(deal.id); }}
            onCancel={(e) => { e?.stopPropagation?.(); setPopOpen(false); }}
            okText="Löschen"
            cancelText="Abbrechen"
            okButtonProps={{ danger: true }}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setPopOpen(true); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '10px 14px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 14,
                fontWeight: 500,
                color: '#E83838',
                textAlign: 'left',
                transition: 'background 120ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#FFF5F5'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                <path d="M3 4h8M5.5 4V3a1 1 0 011-1h1a1 1 0 011 1v1M6 6.5v3M8 6.5v3M4 4l.5 7a1 1 0 001 1h3a1 1 0 001-1L10 4"
                  stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Löschen
            </button>
          </Popconfirm>
        </div>
      )}
    </div>
  );
}

/* ── Deal Card ── */
function DealCard({ deal, rawListing, onClick, onDelete, onAddToCompare, isSelected, isSelecting, isDisabled, onEdit }) {
  const rules = useRules();
  const [hovered, setHovered] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const isTop  = deal.tier === 'top';
  const isWeak = deal.tier === 'weak';

  // During selection mode: card body click toggles checkbox, not navigation
  const handleCardClick = (e) => {
    if (isSelecting) {
      e.stopPropagation();
      if (!isDisabled || isSelected) {
        onAddToCompare(deal.id);
      }
      return;
    }
    onClick();
  };

  const showCheckbox = hovered || isSelected || isSelecting;

  return (
    <div
      onClick={handleCardClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`
        relative bg-white rounded-2xl border p-5 cursor-pointer
        transition-all duration-200 animate-fade-up
        hover:shadow-lg hover:-translate-y-0.5
        ${isTop  ? 'border-brand-100 shadow-[0_0_0_1px_rgba(14,116,144,.08),0_2px_12px_rgba(8,145,178,.07)]' : 'border-gray-100'}
        ${isWeak ? 'opacity-70 saturate-[.8] hover:opacity-100 hover:saturate-100' : ''}
      `}
      style={isSelected ? {
        border: '2px solid #1DB3B3',
        boxShadow: '0 0 0 3px rgba(29,179,179,0.12)',
      } : {}}
    >
      {/* Selected background tint overlay */}
      {isSelected && (
        <div style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          background: 'rgba(229,245,241,0.40)',
          pointerEvents: 'none',
          zIndex: 0,
        }} />
      )}

      {/* TOP DEAL badge — top-0 right-0, no positional conflict with kebab */}
      {isTop && (
        <div
          className="absolute top-0 right-0 bg-brand-400 text-white text-[9px] font-extrabold tracking-wider px-3 py-1 rounded-bl-lg rounded-tr-2xl"
          style={{ zIndex: 2 }}
        >
          TOP DEAL
        </div>
      )}

      {/* Selection checkbox — top-3 left-3, hover-only on desktop, always visible when selected */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 10,
          opacity: showCheckbox ? 1 : 0,
          transition: 'opacity 150ms',
          pointerEvents: showCheckbox ? 'auto' : 'none',
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (!isDisabled || isSelected) onAddToCompare(deal.id);
        }}
      >
        <span
          title={isDisabled && !isSelected ? 'Max. 4 Deals vergleichbar' : undefined}
          style={{
            width: 18,
            height: 18,
            borderRadius: 4,
            border: isSelected ? '1.5px solid #1DB3B3' : '1.5px solid #ACACB8',
            background: isSelected ? '#1DB3B3' : '#FFFFFF',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isDisabled && !isSelected ? 'not-allowed' : 'pointer',
            opacity: isDisabled && !isSelected ? 0.4 : 1,
            transition: 'all 150ms',
            flexShrink: 0,
          }}
        >
          {isSelected && (
            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
              <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </span>
      </div>

      {/* Kebab 3-dot menu — top-3 right-3, hover-only on desktop */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 11,
          opacity: hovered ? 1 : 0,
          transition: 'opacity 150ms',
          // On touch devices: always visible (handled via CSS below)
        }}
        className="kebab-hover-wrapper"
      >
        <KebabMenu deal={deal} onAddToCompare={onAddToCompare} onDelete={onDelete} />
      </div>

      {/* Card content — layered above overlays */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Top: Score + Address */}
        <div className="flex items-start gap-3.5 mb-3.5">
          <ScoreRing score={deal.score} />
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="flex items-center gap-1 min-w-0">
              <div className="font-bold text-[13.5px] text-gray-900 tracking-tight truncate flex-1 min-w-0">
                {deal.address}
              </div>
              {/* Pencil icon — hover-only (matches KebabMenu opacity pattern) */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditOpen(true);
                }}
                aria-label="Objektinformationen bearbeiten"
                title="Bearbeiten"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 3,
                  borderRadius: 4,
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: hovered ? 1 : 0,
                  transition: 'opacity 150ms, background 150ms',
                  color: '#8C8FA0',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(29, 179, 179, 0.1)';
                  e.currentTarget.style.color = '#1DB3B3';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = '#8C8FA0';
                }}
                className="card-pencil-btn"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M8.5 1.5L10.5 3.5L4 10H2V8L8.5 1.5Z"
                    stroke="currentColor" strokeWidth="1.2"
                    strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="text-[11.5px] text-gray-400">{deal.city}</div>
          </div>
        </div>

        {/* Edit Object Modal */}
        {editOpen && (
          <EditObjectModal
            deal={deal}
            raw={rawListing}
            open={editOpen}
            onClose={() => setEditOpen(false)}
            onSave={(updatedListing) => {
              setEditOpen(false);
              onEdit?.(deal.id, updatedListing);
            }}
          />
        )}

        {/* Verdict */}
        <div className="text-[13px] font-semibold text-gray-700 mb-3 leading-snug tracking-tight">
          {getVerdictFromRules(rules, deal.score)?.verdict_sentence ?? ''}
        </div>

        {/* Cashflow Strip */}
        <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5 mb-3">
          <div>
            <div className="flex items-center gap-1 text-[9.5px] font-bold text-gray-400 mb-0.5">
              Cashflow / Monat
              <span
                title="Plugin-Schätzwert: berechnet ohne Tilgung und mit pauschalen Bewirtschaftungskosten. Genaue Werte im Workspace."
                style={{ cursor: 'help', lineHeight: 0 }}
              >
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                  <circle cx="5.5" cy="5.5" r="4.75" stroke="#1DB3B3" strokeWidth="1.2" fill="none"/>
                  <path d="M5.5 4.5v3" stroke="#1DB3B3" strokeWidth="1.2" strokeLinecap="round"/>
                  <circle cx="5.5" cy="3.25" r="0.5" fill="#1DB3B3"/>
                </svg>
              </span>
            </div>
            <div className={`font-sans text-base font-semibold tracking-tight ${deal.cf !== null && deal.cf >= 0 ? 'text-emerald-600' : 'text-gray-700'}`}>
              {deal.cf !== null ? fmtCf(deal.cf) : '\u2014'}
            </div>
          </div>
          <div className="text-right">
            <span className="block text-[9.5px] font-bold text-gray-400">Kaufpreis</span>
            <span className="block text-[13px] font-semibold text-gray-600">{fmt(deal.price)} €</span>
          </div>
        </div>

        {/* Source */}
        <div className="text-[10px] text-gray-400 mb-2">
          {deal.source === 'Immoscout' ? 'ImmobilienScout24'
           : deal.source === 'Kleinanzeigen' ? 'Kleinanzeigen'
           : deal.source === 'immowelt' ? 'Immowelt'
           : deal.source}
        </div>

        {/* CTA */}
        <button className={`w-full border-none rounded-lg py-2.5 font-semibold text-[13px] text-white flex items-center justify-center gap-1.5 transition-colors ${
          isTop ? 'bg-brand-400 hover:bg-brand-700' : 'bg-gray-900 hover:bg-gray-700'
        }`}>
          {deal.ctaLabel}
        </button>
      </div>
    </div>
  );
}

/* ── Floating Action Bar ── */
function FloatingActionBar({ count, onCompare, onClear }) {
  const canCompare = count >= 2;
  const atMax      = count >= MAX_COMPARE;

  return (
    <div style={{
      position: 'sticky',
      top: 0,
      zIndex: 50,
      background: '#FFFFFF',
      borderBottom: '1px solid #E0E0DF',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginLeft: -28,
      marginRight: -28,
      paddingLeft: 28,
      paddingRight: 28,
      marginBottom: 16,
    }}>
      {/* Left: count label + hint */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="8" cy="8" r="7" stroke="#1DB3B3" strokeWidth="1.5"/>
          <path d="M5 8L7 10.5L11 5.5" stroke="#1DB3B3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#1F1F1F', whiteSpace: 'nowrap' }}>
          {count} {count === 1 ? 'Deal' : 'Deals'} ausgewählt{atMax ? ' (Maximum)' : ''}
        </span>
        {!canCompare && (
          <span style={{ fontSize: 12, fontWeight: 400, color: '#8C8FA0', marginLeft: 4, whiteSpace: 'nowrap' }}>
            &mdash; Mindestens 2 Deals für Vergleich
          </span>
        )}
      </div>

      {/* Right: actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <button
          onClick={onClear}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 500,
            color: '#838799',
            padding: '4px 8px',
            borderRadius: 6,
            transition: 'color 120ms',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#5D5D6D'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#838799'; }}
        >
          Abbrechen
        </button>
        <button
          onClick={canCompare ? onCompare : undefined}
          disabled={!canCompare}
          style={{
            background: canCompare ? '#1DB3B3' : '#C8C8D0',
            color: canCompare ? '#FFFFFF' : '#8C8FA0',
            border: 'none',
            borderRadius: 6,
            padding: '8px 16px',
            fontSize: 14,
            fontWeight: 600,
            cursor: canCompare ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit',
            transition: 'background 150ms',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => { if (canCompare) e.currentTarget.style.background = '#157F7F'; }}
          onMouseLeave={e => { if (canCompare) e.currentTarget.style.background = '#1DB3B3'; }}
        >
          {canCompare ? `${count} Deals vergleichen \u2192` : 'Vergleichen \u2192'}
        </button>
      </div>
    </div>
  );
}

/* ── Filter Chip ── */
function FilterChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px',
        borderRadius: 99,
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        border: `1px solid ${active ? CYAN_ACTIVE_BORDER : '#C8C8D0'}`,
        background: active ? CYAN_ACTIVE_BG : '#FFFFFF',
        color: active ? CYAN : '#838799',
        cursor: 'pointer',
        height: 32,
        display: 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        transition: 'all 0.12s',
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );
}

/* ── Sort Dropdown ── */
function SortDropdown({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        height: 32,
        padding: '0 28px 0 10px',
        borderRadius: 8,
        border: '1px solid #C8C8D0',
        background: '#FFFFFF',
        color: '#5D5D6D',
        fontSize: 13,
        fontWeight: 500,
        fontFamily: 'inherit',
        cursor: 'pointer',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23ACACB8' stroke-width='1.3' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 8px center',
        outline: 'none',
        flexShrink: 0,
      }}
      onFocus={e => { e.currentTarget.style.borderColor = CYAN; }}
      onBlur={e => { e.currentTarget.style.borderColor = '#C8C8D0'; }}
    >
      <option value="score">Score (höchster zuerst)</option>
      <option value="price">Kaufpreis (niedrigster)</option>
      <option value="cf">Cashflow (höchster)</option>
      <option value="saved">Zuletzt gespeichert</option>
    </select>
  );
}

/* ── Filter group separator ── */
function FilterSep() {
  return (
    <span style={{
      display: 'inline-block',
      width: 1,
      height: 20,
      background: '#C8C8D0',
      flexShrink: 0,
      alignSelf: 'center',
      margin: '0 4px',
    }} />
  );
}

/* ── Apply filters and sort ── */
function applyFiltersAndSort(deals, portalFilter, priceFilter, sortMode) {
  let result = deals;

  if (portalFilter === 'pos') {
    result = result.filter(d => d.cf !== null && d.cf >= 0);
  } else if (portalFilter === 'is24') {
    result = result.filter(d => d.source === 'Immoscout');
  } else if (portalFilter === 'ka') {
    result = result.filter(d => d.source === 'Kleinanzeigen');
  }

  if (priceFilter === 'sub200') {
    result = result.filter(d => d.price < 200000);
  } else if (priceFilter === '200-500') {
    result = result.filter(d => d.price >= 200000 && d.price < 500000);
  } else if (priceFilter === '500plus') {
    result = result.filter(d => d.price >= 500000);
  }

  result = [...result].sort((a, b) => {
    if (sortMode === 'price') return a.price - b.price;
    if (sortMode === 'cf') {
      if (a.cf === null && b.cf === null) return 0;
      if (a.cf === null) return 1;
      if (b.cf === null) return -1;
      return b.cf - a.cf;
    }
    if (sortMode === 'saved') {
      const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return db - da;
    }
    // default: score desc
    if (a.score === null && b.score === null) return 0;
    if (a.score === null) return 1;
    if (b.score === null) return -1;
    return b.score - a.score;
  });

  return result;
}

/* ── Dashboard ── */
export default function Dashboard() {
  const navigate = useNavigate();
  const auth     = useAuth();
  const userID   = auth.user?.profile?.sub;

  const [portalFilter, setPortalFilter] = useState('all');
  const [priceFilter,  setPriceFilter]  = useState(null);
  const [sortMode,     setSortMode]     = useState('score');

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const isSelecting = selectedIds.size > 0;

  const [deleteListing] = useDeleteListingMutation();
  const { data, isLoading, isError } = useGetAllListingEnrichedQuery(userID, { skip: !userID });

  const rawListings = data?.listings || [];
  const deals       = rawListings.map(mapListing);

  const anyFilterActive = portalFilter !== 'all' || priceFilter !== null;
  const sorted          = applyFiltersAndSort(deals, portalFilter, priceFilter, sortMode);

  const resetFilters = () => {
    setPortalFilter('all');
    setPriceFilter(null);
    setSortMode('score');
  };

  const handleDelete = async (listingId) => {
    // Remove from selection if the deleted deal was selected
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(listingId);
      return next;
    });
    try {
      await deleteListing({ UserID: userID, ListingID: listingId }).unwrap();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < MAX_COMPARE) {
        next.add(id);
      }
      return next;
    });
  };

  const handleCompare = () => {
    const ids = Array.from(selectedIds).join(',');
    navigate(`/comparison?ids=${ids}`);
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  if (isLoading) return (
    <div className="px-7 py-5">
      <div className="text-center py-20 text-gray-400 text-sm">Listings werden geladen\u2026</div>
    </div>
  );

  if (isError) return (
    <div className="px-7 py-5">
      <div className="text-center py-20 text-gray-400 text-sm">Fehler beim Laden der Listings.</div>
    </div>
  );

  return (
    <div className="px-7 py-5">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-extrabold text-gray-900 tracking-tight mb-1">
          Deine gespeicherten Objekte
        </h1>
        <p className="text-[13.5px] text-gray-500">
          {deals.length} {deals.length === 1 ? 'Objekt' : 'Objekte'} gespeichert
        </p>
      </div>

      {/* Filter area */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <FilterChip label="Alle"               active={portalFilter === 'all'}  onClick={() => setPortalFilter('all')} />
            <FilterChip label="Positiver Cashflow" active={portalFilter === 'pos'}  onClick={() => setPortalFilter(portalFilter === 'pos'  ? 'all' : 'pos')} />
            <FilterChip label="ImmobilienScout24"  active={portalFilter === 'is24'} onClick={() => setPortalFilter(portalFilter === 'is24' ? 'all' : 'is24')} />
            <FilterChip label="Kleinanzeigen"      active={portalFilter === 'ka'}   onClick={() => setPortalFilter(portalFilter === 'ka'   ? 'all' : 'ka')} />
          </div>
          <div style={{ flex: 1 }} />
          <SortDropdown value={sortMode} onChange={setSortMode} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'auto' }}>
            <FilterChip label="bis 200k"  active={priceFilter === 'sub200'}  onClick={() => setPriceFilter(priceFilter === 'sub200'  ? null : 'sub200')} />
            <FilterChip label="200k-500k" active={priceFilter === '200-500'} onClick={() => setPriceFilter(priceFilter === '200-500' ? null : '200-500')} />
            <FilterChip label="500k+"     active={priceFilter === '500plus'} onClick={() => setPriceFilter(priceFilter === '500plus' ? null : '500plus')} />
          </div>
        </div>
      </div>

      {/* Floating Action Bar — appears when >= 1 deal selected */}
      {isSelecting && (
        <FloatingActionBar
          count={selectedIds.size}
          onCompare={handleCompare}
          onClear={handleClearSelection}
        />
      )}

      {/* Grid / Empty States */}
      {sorted.length === 0 ? (
        deals.length === 0 ? (
          /* ── Onboarding: 0-Deal-State ── */
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 240px)',
            textAlign: 'center',
          }}>
            <div style={{
              width: 64,
              height: 64,
              background: '#E8F7F7',
              borderRadius: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 24,
              flexShrink: 0,
            }}>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd"
                  d="M14 20a6 6 0 100-12 6 6 0 000 12zm0-2a4 4 0 100-8 4 4 0 000 8z" fill="#4FA5A3"/>
                <path d="M19.5 18.5L24 23" stroke="#4FA5A3" strokeWidth="2" strokeLinecap="round"/>
                <path d="M7 13l7-6 7 6v9a1 1 0 01-1 1H8a1 1 0 01-1-1v-9z"
                  stroke="#4FA5A3" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 600, lineHeight: '30px', color: '#1F1F1F', margin: '0 0 8px 0' }}>
              Noch keine Deals gespeichert
            </h2>
            <p style={{ fontSize: 14, fontWeight: 400, lineHeight: '20px', color: '#777888', maxWidth: 400, margin: '0 0 24px 0' }}>
              Installiere das ImmoSight Plugin um Immobilien direkt aus ImmobilienScout24, Kleinanzeigen oder Immowelt zu analysieren und zu speichern.
            </p>
            <a
              href="https://chromewebstore.google.com/detail/immolens-%E2%80%93-immobilien-che/nkckdnigaikighmfhjighclielepmobl"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                background: CYAN,
                color: '#FFFFFF',
                fontSize: 16,
                fontWeight: 600,
                lineHeight: '24px',
                textDecoration: 'none',
                borderRadius: 8,
                padding: '12px 20px',
                minWidth: 180,
                marginBottom: 12,
                transition: 'background 150ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = CYAN_HOVER; }}
              onMouseLeave={e => { e.currentTarget.style.background = CYAN; }}
            >
              Plugin installieren \u2192
            </a>
          </div>
        ) : (
          /* ── Filter-Leer-State ── */
          <div className="text-center py-20">
            <div style={{ fontSize: 14, color: '#8C8FA0', marginBottom: 8 }}>
              Keine Objekte für diese Filterkombination.
            </div>
            {anyFilterActive && (
              <button
                onClick={resetFilters}
                style={{
                  background: 'none',
                  border: 'none',
                  color: CYAN,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  padding: 0,
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                }}
              >
                Filter zurücksetzen
              </button>
            )}
          </div>
        )
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
          {sorted.map((deal) => (
            <DealCard
              key={deal.id}
              deal={deal}
              rawListing={rawListings.find(r => r.ListingID === deal.id) || null}
              onClick={() => navigate(`/properties/${deal.id}`)}
              onDelete={handleDelete}
              onAddToCompare={handleToggleSelect}
              isSelected={selectedIds.has(deal.id)}
              isSelecting={isSelecting}
              isDisabled={selectedIds.size >= MAX_COMPARE && !selectedIds.has(deal.id)}
              onEdit={(listingId, updatedListing) => {
                // RTK Query invalidates Listing tag after mutation — refetch is automatic.
                // onEdit callback is a no-op here but available for future use.
              }}
            />
          ))}
        </div>
      )}

      {/* Kebab: on touch devices always visible, on desktop hover-only */}
      <style>{`
        @media (hover: none) {
          .kebab-hover-wrapper {
            opacity: 1 !important;
          }
          /* On touch devices: pencil icon always visible */
          .card-pencil-btn {
            opacity: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}
